/**
 * 滑动窗口速率统计。
 *
 * 为什么不能用"总量 ÷ 运行时长"：断链之后那个平均值会慢慢降，看起来像"链路还在但很慢"，
 * 而事实是"已经断了"。界面上的链路速率必须**如实**反映最近这一小段时间，
 * 所以用按秒分桶的滑动窗口 —— 超出窗口的样本直接作废，断链时读数立刻掉到零。
 *
 * 分桶按**绝对秒序**（`floor(时间 / 1000)`）索引而不是循环数组下标：
 * 循环数组的"回收"会把窗口整体错开一格（最新一桶被算成未来的一秒），
 * 边界上读出的速率偏低且难以解释。按秒序存一个最多 N 项的表，逻辑一眼可验。
 */

export interface RateSample {
  /** 最近一个窗口内的每秒样本数 */
  readonly perSecond: number;
  /** 窗口内累计数量 */
  readonly total: number;
}

interface Bucket {
  count: number;
  bytes: number;
}

export class RateMeter {
  private readonly windowMs: number;
  private readonly buckets = new Map<number, Bucket>();
  private startedAt: number;
  private lastRecordedAt: number;

  constructor(now: number, windowSeconds = 5) {
    this.windowMs = Math.max(1000, windowSeconds * 1000);
    this.startedAt = now;
    this.lastRecordedAt = now;
  }

  private secondOf(at: number): number {
    return Math.floor(at / 1000);
  }

  record(now: number, bytes = 0): void {
    const second = this.secondOf(now);
    const bucket = this.buckets.get(second);
    if (bucket === undefined) {
      this.buckets.set(second, { count: 1, bytes });
    } else {
      bucket.count += 1;
      bucket.bytes += bytes;
    }
    this.lastRecordedAt = now;

    const cutoff = second - Math.ceil(this.windowMs / 1000);
    for (const key of [...this.buckets.keys()]) {
      if (key < cutoff) this.buckets.delete(key);
    }
  }

  /**
   * 除数：窗口长度与"实际已运行时长"的较小值。
   *
   * 不能一律除以完整窗口 —— 链路刚连上 1 秒时报"4 帧/秒"而实际是 20 帧/秒，
   * 会让人以为链路有问题。同时至少按 1 秒算，免得刚收到两帧就读出"200 帧/秒"。
   */
  private divisorSeconds(now: number): number {
    const runningSeconds = Math.max(1, (now - this.startedAt) / 1000);
    return Math.min(runningSeconds, this.windowMs / 1000);
  }

  private totals(now: number): { count: number; bytes: number } {
    // 窗口是 [now - windowMs, now]，含当前这一秒
    const oldestKept = this.secondOf(now - this.windowMs);
    const currentSecond = this.secondOf(now);
    let count = 0;
    let bytes = 0;
    for (const [second, bucket] of this.buckets) {
      if (second < oldestKept || second > currentSecond) continue;
      count += bucket.count;
      bytes += bucket.bytes;
    }
    return { count, bytes };
  }

  rate(now: number): RateSample {
    // 超过一个完整窗口没有新样本：直接归零，而不是让平均值慢慢衰减
    if (now - this.lastRecordedAt > this.windowMs) return { perSecond: 0, total: 0 };
    const { count } = this.totals(now);
    return { perSecond: count / this.divisorSeconds(now), total: count };
  }

  bytesPerSecond(now: number): number {
    if (now - this.lastRecordedAt > this.windowMs) return 0;
    return this.totals(now).bytes / this.divisorSeconds(now);
  }

  reset(now: number): void {
    this.buckets.clear();
    this.lastRecordedAt = now;
    this.startedAt = now;
  }
}
